import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import axiosInstance from '../../api/axios';
import { API_ENDPOINTS } from '../../api/endpoints';
import { getApiErrorMessage, resolve403ErrorMessage } from '../../request/getApiErrorMessage.ts';
import { SOX_CYCLES_QUERY_KEY, cycleProcessesQueryKey, processesQueryKey } from '../../queryKeys.ts';

export function useUpdateProcessMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ processId, payload }) => {
      const response = await axiosInstance.patch(API_ENDPOINTS.patchProcess(processId), payload);
      return response.data;
    },
    onSuccess: async (_data, { processId, cycleId }) => {
      toast.success('Process updated');
      const invalidations = [
        queryClient.invalidateQueries({ queryKey: cycleProcessesQueryKey(cycleId) }),
        queryClient.invalidateQueries({ queryKey: processesQueryKey(cycleId) }),
        queryClient.invalidateQueries({ queryKey: SOX_CYCLES_QUERY_KEY })
      ];
      if (processId) {
        invalidations.push(queryClient.invalidateQueries({ queryKey: ['process', processId] }));
      }
      await Promise.all(invalidations);
    },
    onError: (error) => {
      toast.error(resolve403ErrorMessage(error, getApiErrorMessage(error, 'Failed to update process')));
    }
  });
}

export function useCreateProcessMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ cycleId, payload }) => {
      const response = await axiosInstance.post(API_ENDPOINTS.createProcess, {
        cycle_id: cycleId,
        ...payload
      });
      return response.data;
    },
    onSuccess: async (_data, { cycleId }) => {
      toast.success('Process created');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: cycleProcessesQueryKey(cycleId) }),
        queryClient.invalidateQueries({ queryKey: processesQueryKey(cycleId) }),
        queryClient.invalidateQueries({ queryKey: SOX_CYCLES_QUERY_KEY })
      ]);
    },
    onError: (error) => {
      toast.error(resolve403ErrorMessage(error, getApiErrorMessage(error, 'Failed to create process')));
    }
  });
}
